/* eslint-disable no-console */
import produce from 'immer';
import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import { START_PAGINATION_PAGE } from '../../FinancialData/shared/CompanyContext/CompanyContext';
import { TabNamesEnum } from '../BenchmarkPage/BenchmarkTabs/BenchmarkTabs';
import {
  BenchmarkContextActions,
  BenchmarkContextActionTypes,
  BenchmarkContextProps,
  BenchmarkProviderProps,
} from './BenchmarkContext.types';

const BenchmarkContext = createContext<BenchmarkContextProps | undefined>(undefined);

const BenchmarkDispatchContext = createContext<Dispatch<BenchmarkContextActions> | undefined>(
  undefined,
);

const initialBenchmarkState: BenchmarkContextProps = {
  activeBenchmark: undefined,
  activeCompanyId: undefined,
  dataset: undefined,
  companies: [],
  pagination: { page: START_PAGINATION_PAGE, size: 50, totalPages: 0, totalElements: 0 },
  isLoading: false,
  activeTab: TabNamesEnum.SUMMARY,
  testedParty: { uploaded: false, companyId: undefined },
  refreshKey: 0,
  companyFilter: '',
};

const BenchmarkReducer = (state: BenchmarkContextProps, action: BenchmarkContextActions) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('ACTION SENT: ', action);
  }

  switch (action.type) {
    case BenchmarkContextActionTypes.ResetBenchmark: {
      return produce(state, (draft) => {
        draft.activeBenchmark = initialBenchmarkState.activeBenchmark;
        draft.activeCompanyId = initialBenchmarkState.activeCompanyId;
        draft.companies = initialBenchmarkState.companies;
        draft.pagination = initialBenchmarkState.pagination;
        draft.testedParty = initialBenchmarkState.testedParty;
        draft.companyFilter = '';
      });
    }
    case BenchmarkContextActionTypes.SetActiveBenchmark: {
      return produce(state, (draft) => {
        draft.activeBenchmark = action.payload;
        draft.testedParty = {
          uploaded: !!action.payload?.testedPartyUri,
          companyId: action.payload?.testedPartyCompanyId,
        };
        draft.companies = initialBenchmarkState.companies;
        draft.dataset = action.payload?.dataset;
        draft.companyFilter = '';
      });
    }
    case BenchmarkContextActionTypes.SetPagination: {
      return produce(state, (draft) => {
        draft.pagination = action.payload;
      });
    }
    case BenchmarkContextActionTypes.SetIsLoadingState: {
      return produce(state, (draft) => {
        draft.isLoading = action.payload;
      });
    }
    case BenchmarkContextActionTypes.SetBenchmarkCompanies: {
      return produce(state, (draft) => {
        draft.companies = action.payload.companies;
        draft.benchwiseState = action.payload.benchwiseState;
      });
    }
    case BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId: {
      return produce(state, (draft) => {
        draft.activeCompanyId = action.payload;
      });
    }
    case BenchmarkContextActionTypes.SetClickedBenchmarkTabName: {
      return produce(state, (draft) => {
        draft.activeTab = action.payload;
      });
    }
    case BenchmarkContextActionTypes.SetTestedParty: {
      return produce(state, (draft) => {
        draft.testedParty = action.payload;
      });
    }
    case BenchmarkContextActionTypes.IncrementRefreshKey: {
      return produce(state, (draft) => {
        draft.refreshKey = (draft.refreshKey || 0) + 1;
      });
    }
    case BenchmarkContextActionTypes.SetCompanyFilter: {
      return produce(state, (draft) => {
        draft.companyFilter = action.payload;
        draft.pagination = { ...draft.pagination, page: 1 };
      });
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

export const BenchmarkContextProvider = ({ children }: BenchmarkProviderProps) => {
  const [state, dispatch] = useReducer(BenchmarkReducer, initialBenchmarkState);

  return (
    <BenchmarkContext.Provider value={state}>
      <BenchmarkDispatchContext.Provider value={dispatch}>
        {children}
      </BenchmarkDispatchContext.Provider>
    </BenchmarkContext.Provider>
  );
};

export const useBenchmarkState = () => {
  const context = useContext(BenchmarkContext);

  if (context === undefined) {
    throw new Error('BenchmarkState must be used within a BenchmarkProvider');
  }

  return context;
};

export const useBenchmarkDispatch = () => {
  const context = useContext(BenchmarkDispatchContext);

  if (context === undefined) {
    throw new Error('BenchmarkDispatch must be used within a BenchmarkProvider');
  }

  return context;
};
